import HotTable from '@handsontable/react-wrapper';
import { Alert, Button } from '@mui/material';
import { ColumnSettings } from 'handsontable/settings';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import {
  HedgePlanExecutionType,
  HedgePlanTradeModel,
  UpdateHedgePlanTradeModel,
  UpdateHedgePlanTradesRequest,
} from '../../../../Generated/Commodity-Management-Api';
import { StoneXRow } from '../../../StoneX';
import { StoneXButton } from '../../../StoneXMui';
import './HedgePlanGridValidators';
import { isHedgePlanSourceRowEmpty } from './HedgePlanGridValidators';
import HedgePlanSeasonTabLayout from './HedgePlanSeasonTabLayout';
import { SeasonPeriod } from './HedgePlanTerms';
import { fromTriggerLabel, getTradeTriggerLabel, getTriggerLabels, isMissingTriggerLabel, SeasonTriggers } from './HedgePlanTradeTriggers';

interface HedgePlanTradesTabProps {
  onClose: () => void;
  onSaveFailed: (message: string) => void;
  onSaved: (period: SeasonPeriod, trades: HedgePlanTradeModel[]) => void;
  hedgePlanId: number;
  period: SeasonPeriod;
  trades: HedgePlanTradeModel[];
  // Trades are entered as a volume when the plan has a budget source, and as a percent otherwise.
  byVolume: boolean;
  // The season report's trades, which carry both percent and volume, for showing a trade in the other unit.
  reportTrades?: HedgePlanTradeModel[];
  // The season's triggers, which a trade can say it fills.
  triggers: SeasonTriggers;
}

const EXECUTION_TYPE_COLUMN_WIDTH = 130;
const TRADE_DATE_COLUMN_WIDTH = 130;
const VOLUME_COLUMN_WIDTH = 110;
const PRICE_COLUMN_WIDTH = 110;
const TRIGGER_COLUMN_WIDTH = 190;
const DELETE_COLUMN_WIDTH = 40;

const EXECUTION_TYPES: HedgePlanExecutionType[] = ['Value', 'Budget', 'Time'];

const HEADER_HEIGHT = 48;
const ROW_HEIGHT = 40;
// Handsontable sizes a dropdown/date editor's popup to fit within its OWN grid's workspace
// height, not the modal or page - with too few rows that workspace is too short for the popup
// to expand into. minRows pads the grid with empty rows up to this floor so height="auto" always
// gives Handsontable enough workspace, without us computing pixel heights by hand.
const MIN_VISIBLE_ROWS = 5;

interface TradeRow {
  hedgePlanTradeId?: number | null;
  executionType: HedgePlanExecutionType | null;
  tradeDate: string | null;
  // A percent or a volume, depending on the plan's trade entry unit.
  amount: number | null;
  price: number | null;
  // The label of the trigger the trade fills, from the triggers of its execution type.
  trigger: string | null;
  deleteAction?: null;
}

const dataSchema: TradeRow = { hedgePlanTradeId: null, executionType: null, tradeDate: null, amount: null, price: null, trigger: null, deleteAction: null };

function deleteRowRenderer(instance: any, tableCell: HTMLTableCellElement, row: number) {
  tableCell.replaceChildren();
  if (row === instance.countRows() - 1) {
    return;
  }

  const deleteButton = document.createElement('span');
  deleteButton.innerText = 'X';
  deleteButton.title = 'Delete row';
  deleteButton.setAttribute('role', 'button');
  deleteButton.setAttribute('aria-label', 'Delete row');
  deleteButton.style.cursor = 'pointer';
  deleteButton.style.color = 'var(--font-color)';
  deleteButton.onclick = () => instance.alter('remove_row', row);
  tableCell.appendChild(deleteButton);
}

const deleteColumn: ColumnSettings = {
  data: 'deleteAction',
  width: DELETE_COLUMN_WIDTH,
  readOnly: true,
  className: 'htCenter htMiddle',
  renderer: deleteRowRenderer,
};

// Lists the season's triggers of the row's execution type, plus the row's own trigger if the season no longer has it.
function buildTriggerColumn(triggers: SeasonTriggers): ColumnSettings {
  function rowLabels(cellProperties: any): { labels: string[]; current: string | null } {
    const executionType = cellProperties.instance.getDataAtRowProp(cellProperties.row, 'executionType') as HedgePlanExecutionType | null;
    const current = cellProperties.instance.getDataAtRowProp(cellProperties.row, 'trigger') as string | null;
    return { labels: executionType ? getTriggerLabels(executionType, triggers) : [], current };
  }

  return {
    data: 'trigger',
    width: TRIGGER_COLUMN_WIDTH,
    type: 'dropdown',
    filter: false,
    source(this: any, _query: string, process: (items: string[]) => void) {
      const { labels, current } = rowLabels(this);
      process(isMissingTriggerLabel(current) ? [current as string, ...labels] : labels);
    },
    validator(this: any, value: string | null, callback: (valid: boolean) => void) {
      callback(!value || isMissingTriggerLabel(value) || rowLabels(this).labels.includes(value));
    },
  };
}

function buildColumns(byVolume: boolean, triggers: SeasonTriggers): ColumnSettings[] {
  return [
    { data: 'hedgePlanTradeId', readOnly: true, className: 'htHidden' },
    { data: 'executionType', width: EXECUTION_TYPE_COLUMN_WIDTH, type: 'dropdown', source: EXECUTION_TYPES, validator: 'executionTypeValidator' },
    { data: 'tradeDate', width: TRADE_DATE_COLUMN_WIDTH, type: 'date', dateFormat: 'YYYY-MM-DD', correctFormat: true, validator: 'dateValidator' },
    byVolume
      ? { data: 'amount', width: VOLUME_COLUMN_WIDTH, type: 'numeric', numericFormat: { pattern: '0,0' }, className: 'numeric', validator: 'volumeValidator' }
      : { data: 'amount', width: VOLUME_COLUMN_WIDTH, type: 'numeric', className: 'numeric', validator: 'percentValidator' },
    { data: 'price', width: PRICE_COLUMN_WIDTH, type: 'numeric', numericFormat: { pattern: '0,0.0000' }, className: 'numeric' },
    buildTriggerColumn(triggers),
    deleteColumn,
  ];
}

function displayDate(value: string | null | undefined): string | null {
  return value ? value.substring(0, 10) : null;
}

function validateCells(hotInstance: any): Promise<boolean> {
  return new Promise((resolve) => hotInstance.validateCells((valid: boolean) => resolve(valid)));
}

export default function HedgePlanTradesTab(props: HedgePlanTradesTabProps) {
  const [modalSessionKey, setModalSessionKey] = useState(0);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const hotRef = useRef<any>(null);

  useEffect(() => {
    setModalSessionKey((current) => current + 1);
    setValidationError(undefined);
  }, [props.period.key]);

  const selectedPeriod = props.period;
  const byVolume = props.byVolume;
  const seasonTriggers = useMemo<SeasonTriggers>(() => {
    const matchesPeriod = (trigger: { year: number; term: string }) => trigger.year === selectedPeriod.year && trigger.term === selectedPeriod.term;
    return {
      value: props.triggers.value.filter(matchesPeriod),
      budget: props.triggers.budget.filter(matchesPeriod),
      time: props.triggers.time.filter(matchesPeriod),
    };
  }, [props.triggers, selectedPeriod]);
  const columns = useMemo(() => buildColumns(byVolume, seasonTriggers), [byVolume, seasonTriggers]);
  const headers = ['Id', 'Execution Type', 'Trade Date', byVolume ? 'Volume' : 'Volume %', 'Price', 'Trigger Filled', ''];
  const rows = useMemo(() => {
    const matchesPeriod = (trade: { year: number; term: string }) => trade.year === selectedPeriod.year && trade.term === selectedPeriod.term;
    return props.trades.filter(matchesPeriod).map((trade) => {
      // A trade entered in the other unit shows the report's conversion, and is saved in this unit.
      const reportTrade = props.reportTrades?.find((candidate) => candidate.hedgePlanTradeId === trade.hedgePlanTradeId);
      const amount = byVolume ? trade.volume ?? reportTrade?.volume : trade.volumePercent ?? reportTrade?.volumePercent;
      return {
        hedgePlanTradeId: trade.hedgePlanTradeId,
        executionType: trade.executionType,
        tradeDate: displayDate(trade.tradeDate),
        amount: amount ?? null,
        price: trade.price ?? null,
        trigger: getTradeTriggerLabel(trade.executionType, trade, seasonTriggers),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, props.trades, props.reportTrades, seasonTriggers, byVolume, modalSessionKey]);

  function close() {
    if (!saving) {
      props.onClose();
    }
  }

  async function save() {
    const hotInstance = hotRef.current?.hotInstance;
    if (!hotInstance) {
      return;
    }

    const valid = await validateCells(hotInstance);
    if (!valid) {
      setValidationError('Some rows are incomplete or invalid. Fix the highlighted cells, or clear the row entirely.');
      return;
    }

    const sourceRows = hotInstance.getSourceData() as TradeRow[];
    const trades: UpdateHedgePlanTradeModel[] = sourceRows
      .filter((row) => !!row.executionType && !!row.tradeDate && row.amount !== null && row.amount !== undefined)
      .map((row) => ({
        hedgePlanTradeId: row.hedgePlanTradeId,
        executionType: row.executionType as HedgePlanExecutionType,
        tradeDate: row.tradeDate as string,
        volumePercent: byVolume ? null : Number(row.amount),
        volume: byVolume ? Number(row.amount) : null,
        // A cleared cell reads as '', which Number() would turn into a price of 0.
        price: row.price === null || row.price === undefined || String(row.price).trim() === '' ? null : Number(row.price),
        ...fromTriggerLabel(row.executionType as HedgePlanExecutionType, row.trigger),
      }));

    const request: UpdateHedgePlanTradesRequest = { hedgePlanId: props.hedgePlanId, year: selectedPeriod.year, term: selectedPeriod.term, trades };

    setSaving(true);
    setValidationError(undefined);
    try {
      const response = await CommodityManagementApi.updateHedgePlanTrades(undefined, String(props.hedgePlanId), String(selectedPeriod.year), request);
      props.onSaved(selectedPeriod, response.data.trades ?? []);
      props.onClose();
    } catch (error: any) {
      const message = error?.response?.data?.detail ?? error?.response?.data?.message ?? 'The trades could not be saved.';
      setValidationError(message);
      props.onSaveFailed(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <HedgePlanSeasonTabLayout
      footer={(
        <StoneXRow justify="end">
          <Button variant="outlined" onClick={close} disabled={saving}>Cancel</Button>
          <StoneXButton onClick={save} isWorking={saving}>Save</StoneXButton>
        </StoneXRow>
      )}
    >
      <StoneXRow align="start">
        <div style={{ minWidth: 0, width: '100%' }}>
          <HotTable
            key={`trades-${selectedPeriod.key}-${modalSessionKey}`}
            ref={hotRef}
            data={rows}
            dataSchema={dataSchema}
            colHeaders={headers}
            columns={columns}
            stretchH="all"
            width="100%"
            height="auto"
            rowHeights={ROW_HEIGHT}
            columnHeaderHeight={HEADER_HEIGHT}
            autoWrapRow
            autoWrapCol
            minRows={MIN_VISIBLE_ROWS}
            minSpareRows={1}
            isEmptyRow={(row) => isHedgePlanSourceRowEmpty(rows[row])}
            contextMenu={['row_above', 'row_below', 'remove_row']}
            hiddenColumns={{ columns: [0], indicators: false, copyPasteEnabled: false }}
            afterChange={(changes, source) => {
              // A trigger belongs to one execution type, so changing the type clears it.
              if (source === 'loadData' || !changes) {
                return;
              }
              changes
                .filter(([, prop, previous, next]) => prop === 'executionType' && previous !== next)
                .forEach(([row]) => hotRef.current?.hotInstance.setDataAtRowProp(row, 'trigger', null));
            }}
            licenseKey="29ae0-f35ad-79c41-2472d-2c70d"
          />
        </div>
      </StoneXRow>

      {validationError && (
        <StoneXRow>
          <Alert severity="error" onClose={() => setValidationError(undefined)}>{validationError}</Alert>
        </StoneXRow>
      )}

    </HedgePlanSeasonTabLayout>
  );
}
